/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the strict publish (docs/SPEC_WORKFLOW.md §7).

   A publish is ONE action that cannot half-land. This module is the pure
   brain of that: what the publish offers each variant (`planPublish`) and
   the one set of records the batch writes (`buildPublishResult` →
   `publishWrites`).

   ⛔ Tweaks 35 (docs/MODULES_VISION.md "Propagation = PR, never silent
   mutation"): the publisher never answers for a variant. Every variant the
   publish touches receives its changes as PENDING offers with its own
   words held (`enforcePublishHold` — the SAME function the API server
   re-runs on the batch), decided later in that variant's Updates panel.
   ═══════════════════════════════════════════════════════════════ */

import type {
  ClientLayer,
  ContentBlock,
  SpecMaster,
  SpecMasterDraft,
  SpecMasterVersion,
} from '../../shared/types';
import {
  buildVariantChangeEntries,
  enforcePublishHold,
  pendingCount,
  type IdContext,
  type VariantChangeEntry,
} from '../../shared/variant-updates';
import { buildInlineDiff } from '../../shared/block-diff';
import { hashBlock } from '../../shared/spec-compose';
import { pushEditHistory } from '../../shared/editHistory';
import type { BatchWrite } from '../services/cloudCache';

export interface PublishPlan {
  /** The published master as the server has it now. */
  master: SpecMaster;
  /** The draft being published (mirror flushed). */
  draft: SpecMasterDraft;
  fromVersion: number;
  toVersion: number;
  /** The layers the publish carries — the draft's, un-held. */
  layers: ClientLayer[];
  /** One entry per variant the publish touches, built from the UN-held layers. */
  entries: VariantChangeEntry[];
  /** Blocks that differ between published and draft (the dialog's count). */
  changedBlocks: number;
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export function planPublish(args: {
  master: SpecMaster;
  draft: SpecMasterDraft;
  by: string;
  ctx?: IdContext;
}): PublishPlan {
  const { master, draft, by, ctx } = args;
  const prevCore = master.coreContent || [];
  const nextCore = draft.coreContent || [];
  // A draft from before migration 044 carries no `layers` — publishing it
  // must never wipe the master's.
  const layers = (draft.layers ?? master.layers ?? []) as ClientLayer[];
  const fromVersion = master.version || 0;
  const toVersion = fromVersion + 1;
  const entries = buildVariantChangeEntries({
    specMasterId: master.id,
    fromVersion,
    toVersion,
    prevCore,
    nextCore,
    layers,
    createdBy: by,
    ctx,
  });
  const changedBlocks = buildInlineDiff(prevCore, nextCore).filter(
    (b) => b.kind !== 'unchanged'
  ).length;
  return { master, draft, fromVersion, toVersion, layers, entries, changedBlocks };
}

/** The variants the publish will OFFER changes to (the dialog lists them):
 *  every entry with a change still to answer (a change on a block the
 *  variant hides arrives already decided). */
export function affectedEntries(plan: Pick<PublishPlan, 'entries'>): VariantChangeEntry[] {
  return plan.entries.filter((e) => pendingCount(e) > 0);
}

export interface PublishResult {
  master: SpecMaster;
  /** Every entry, PENDING — the offers each variant answers in its Updates. */
  entries: VariantChangeEntry[];
}

/** The new master + the pending offers, with every variant's words held. */
export function buildPublishResult(
  plan: PublishPlan,
  opts: { by: string; now?: string; ctx?: IdContext }
): PublishResult {
  const now = opts.now || new Date().toISOString();
  const ctx: IdContext = { ...(opts.ctx || {}), by: opts.by, now };
  const { master, draft } = plan;

  const held = enforcePublishHold({
    specMasterId: master.id,
    fromVersion: plan.fromVersion,
    toVersion: plan.toVersion,
    prevCore: master.coreContent || [],
    nextCore: draft.coreContent || [],
    layers: plan.layers,
    beforeLayers: (master.layers || []) as ClientLayer[],
    entries: plan.entries,
    createdBy: opts.by,
    ctx,
  });
  const layers = held.layers;
  const entries = held.entries;

  const snapshot: SpecMasterVersion = {
    version: master.version,
    snapshot: {
      coreContent: clone(master.coreContent || []),
      systems: clone(master.systems || []),
      title: master.title,
      description: master.description,
    },
    savedBy: master.modifiedBy || 'unknown',
    savedAt: master.lastModified,
  };
  const maxVersions = master.maxVersionsKept || 10;
  const next: SpecMaster = {
    ...master,
    versionHistory: [...(master.versionHistory || []), snapshot].slice(-maxVersions),
    coreContent: clone(draft.coreContent || []) as ContentBlock[],
    systems: clone(draft.systems || []),
    variants: clone(draft.variants || []),
    layers,
    version: plan.toVersion,
    modifiedBy: opts.by,
    status: 'published',
    lastModified: now,
    editHistory: pushEditHistory(master.editHistory, opts.by, undefined, 'published'),
    // The lock is cleared — an explicit `undefined` key serializes as NULL.
    lockedBy: undefined,
    lockedAt: undefined,
  };
  return { master: next, entries };
}

/** The ONE publish transaction, in order: the master row, the pending feed
 *  rows (only when a variant was touched), then every draft row for this
 *  master (the shared one and any pre-collab per-user drafts). */
export function publishWrites(
  result: PublishResult,
  draftIds: string[],
  build: {
    saveMaster: (m: SpecMaster) => BatchWrite;
    createEntries: (e: VariantChangeEntry[]) => BatchWrite;
    removeDraft: (id: string) => BatchWrite;
  }
): BatchWrite[] {
  const writes: BatchWrite[] = [build.saveMaster(result.master)];
  if (result.entries.length) writes.push(build.createEntries(result.entries));
  for (const id of [...new Set(draftIds)]) writes.push(build.removeDraft(id));
  return writes;
}

/** What one entry OFFERS, independent of its random ids: the variant, and
 *  per change the block, the kind and the exact words before/after. Two
 *  entries with the same key ask the variant the same questions. */
export function entryKey(entry: VariantChangeEntry): string {
  const changes = (entry.changes || [])
    .map(
      (c) =>
        `${c.blockId}:${c.kind}:${c.decision === 'pending' ? 'p' : c.decision}:` +
        `${c.beforeBlock ? hashBlock(c.beforeBlock) : '-'}>${c.afterBlock ? hashBlock(c.afterBlock) : '-'}`
    )
    .sort();
  return `${entry.layerId ?? ''}|${changes.join(',')}`;
}

/** The stale-dialog guard: does the dialog still list the same offers
 *  the LIVE draft makes now? Order-free; compares by entry key. */
export function sameOffers(
  answered: Pick<PublishPlan, 'entries'>,
  live: Pick<PublishPlan, 'entries'>
): boolean {
  const a = answered.entries.map(entryKey).sort();
  const b = live.entries.map(entryKey).sort();
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

/** "Publish failed: <message> (step 2)" — the step is 1-based. */
export function publishErrorMessage(err: unknown): string {
  const e = err as { message?: unknown; failedOp?: unknown } | null;
  const msg = typeof e?.message === 'string' && e.message ? e.message : 'Unknown error';
  const step = typeof e?.failedOp === 'number' ? ` (step ${e.failedOp + 1})` : '';
  return `Publish failed: ${msg}${step}`;
}
